import { Controller } from "@hotwired/stimulus"

// Builds export URLs from selected field checkboxes + active page filters.
//
// The controller reads filter values from a sibling form on the page
// (identified by `data-export-fields-filter-form-value`) so that exports
// always honour the current filters — even when Turbo Stream updates
// don't change window.location.
//
// Usage:
//   <div data-controller="export-fields"
//        data-export-fields-filter-form-value="#filters form">
//     <input type="checkbox" data-export-fields-target="checkbox" ...>
//     <a data-export-fields-target="link" data-format="xlsx" ...>
//   </div>
export default class extends Controller {
  static targets = ["checkbox", "link"]
  static values = { filterForm: { type: String, default: "" } }

  connect() {
    this.updateLinks()

    // Intercept clicks on export links to refresh the href right before
    // navigation. This ensures the latest filter values (which may have
    // changed via Turbo Stream or a live filter frame without re-rendering
    // the links) are always captured. "click" covers the keyboard (Enter),
    // which fires no mousedown.
    this.linkTargets.forEach(link => {
      link.addEventListener("mousedown", () => this.updateLinks())
      link.addEventListener("click", () => this.updateLinks())
    })
  }

  updateLinks() {
    const selectedFields = this.checkboxTargets
      .filter(checkbox => checkbox.checked)
      .map(checkbox => checkbox.value)

    const filterParams = this._collectFilterParams()

    this.linkTargets.forEach(link => {
      const format = link.dataset.format
      const basePath = link.dataset.basePath || window.location.pathname
      const url = new URL(basePath, window.location.origin)

      // Append filter params from the page's filter form
      filterParams.forEach(([key, value]) => {
        if (value) url.searchParams.append(key, value)
      })

      // Set export format
      url.searchParams.set("format", format)

      // Append selected fields
      selectedFields.forEach(field => url.searchParams.append("fields[]", field))

      link.href = url.pathname + url.search
    })
  }

  // Reads all non-empty inputs from the filter form on the page.
  // Returns an array of [name, value] pairs.
  _collectFilterParams() {
    if (!this.filterFormValue) return this._paramsFromUrl()

    const form = document.querySelector(this.filterFormValue)
    if (!form) return this._paramsFromUrl()

    const formData = new FormData(form)
    const params = []
    for (const [key, value] of formData.entries()) {
      // Skip empty values, utf8 tokens, and format/commit keys
      if (!value || key === "utf8" || key === "commit" || key === "format") continue
      params.push([key, value])
    }
    return params
  }

  // Fallback: read params from the current URL (works when URL is kept in sync)
  _paramsFromUrl() {
    const url = new URL(window.location.href)
    const params = []
    for (const [key, value] of url.searchParams.entries()) {
      if (key === "format" || key === "fields[]") continue
      if (value) params.push([key, value])
    }
    return params
  }
}
